import React, { Dispatch, SetStateAction } from 'react';
import {
  Avatar,
  Box,
  Button,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { ArrowBackIosRounded } from '@mui/icons-material';
import { GeneralRequests } from '@prisma/client';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { QueryKey } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { getFullName } from '../../helpers/global';
import { getInitials, important } from '../../utils/global/helper';
import { UserConversation } from '../../types/chats';
import DropdownMenu from '../global/DropdownMenu';
import useUser from '../../hooks/global/useUser';
import { FooterAction, LoadedUser } from '../../interfaces';
import { ROUTES } from '../../constants/global';
import useMuteChat from '../../hooks/chat/useMuteChat';
import {
  getArchivedKey,
  getChatParticipants,
  getParticipantDisplayName,
  getParticipantId,
  isConversationArchived,
  updateConversationInList,
} from '../../helpers/chats';
import { getAppointmentStatus } from '../../helpers/appointments';
import useGetChatHeaderAppointment from '../../hooks/chat/useGetChatHeaderAppointment';
import useArchiveChat from '../../hooks/chat/useArchiveChat';
import { resetConversations } from '../../services/chats';

interface Props {
  conversation: UserConversation;
  onBack: () => void;
  queryKey: QueryKey;
  updateConversation: Dispatch<SetStateAction<UserConversation | null>>;
}

const DEFAULT_HEADER = {
  status: '-',
  isRequest: false,
};

function ChatBoxHeader({
  conversation,
  onBack,
  queryKey,
  updateConversation,
}: Props) {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user } = useUser<LoadedUser>();

  const { appointment, appointmentId, isLoading } =
    useGetChatHeaderAppointment(conversation);
  const appointmentImage = appointment?.refImgsId?.[0];

  const { mutate: muteChat, isPending: isMutingChat } = useMuteChat({
    onSuccess: muted => {
      const updatedParticipants = conversation.participants.map(p =>
        p.participantId === user.userId ? { ...p, notify: !muted } : p,
      );
      updateConversationInList(queryKey, {
        ...conversation,
        participants: updatedParticipants,
      });
      toast.success(`Chat ${muted ? 'muted' : 'unmuted'} successfully.`);
    },
    onError: mute => {
      toast.error(`Failed to ${mute ? 'mute' : 'unmute'} chat.`);
    },
  });
  const { mutate: archiveChat, isPending: isArchivingChat } = useArchiveChat({
    onSuccess: archived => {
      toast.success(
        `Chat ${archived ? 'archived' : 'unarchived'} successfully.`,
      );
      const archivedKey = getArchivedKey(user.userId);
      const updatedCustom = {
        ...(conversation.custom ?? {}),
        [archivedKey]: String(archived),
      };
      // since the conversation will no longer be in the current list, we need to update the conversation state
      updateConversation(prev =>
        prev
          ? {
              ...prev,
              custom: updatedCustom,
            }
          : null,
      );
      // choose reset over invalidate to avoid refetching ALL pages the user has scrolled through
      resetConversations();
    },
  });
  const isPending = isMutingChat || isArchivingChat;

  const isArchived = isConversationArchived({
    conversation,
    userId: user.userId,
  });
  const participation = conversation.participants.find(
    p => p.participantId === user.userId,
  );

  const {
    customerParticipant,
    otherParticipants: filteredParticipants,
    initials,
  } = getChatParticipants(conversation, user);

  const customerInitials = appointment?.customer
    ? getInitials(appointment.customer)
    : initials;
  const uniqueParticipantIds = new Set(
    conversation.participants.map(p => p.userId),
  );
  const uniqueParticipants = Array.from(uniqueParticipantIds).map(
    id => conversation.participants.find(p => p.userId === id)!,
  );
  const participantsString = (
    filteredParticipants.length > 0 ? filteredParticipants : uniqueParticipants
  )
    .map(p =>
      getFullName({
        firstName: p.firstName || null,
        lastName: p.lastName ? p.lastName[0] : '',
      }),
    )
    .join(', ');

  const customerName = `${appointment?.customer?.firstName || ''}
          ${
            appointment?.customer?.lastName
              ? `${appointment?.customer?.lastName[0]}.`
              : ''
          }`;

  const { status, isRequest } = React.useMemo(() => {
    if (!appointment) return DEFAULT_HEADER;
    const apptStatus = getAppointmentStatus({
      status: appointment.status,
      previousStatus: appointment.previousStatus,
    });
    if (apptStatus === 'REQUESTED') {
      const requestedOn = new Date(appointment.createdAt || '');
      const requestedText = format(requestedOn, 'MMM d, yyyy');
      return {
        status: `Requested on ${requestedText}`,
        isRequest: true,
      };
    }
    if (apptStatus === 'ACCEPTED') {
      const acceptedOn = new Date(
        appointment.acceptedOn || appointment.updatedAt || '',
      );
      const acceptedText = format(acceptedOn, 'MMM d, yyyy');
      return {
        status: `Accepted on ${acceptedText}`,
        isRequest: true,
      };
    }
    if (apptStatus === 'REJECTED') {
      const acceptedOn = new Date(
        appointment.rejectedOn || appointment.updatedAt || '',
      );
      const acceptedText = format(acceptedOn, 'MMM d, yyyy');
      return {
        status: `Rejected on ${acceptedText}`,
        isRequest: true,
      };
    }
    if (appointment.checkoutSessionCompleted) {
      const completedOn = new Date(appointment.startDateTime || '');
      const completedText = format(completedOn, 'MMM d, yyyy');
      return {
        status: `Completed on ${completedText}`,
        isRequest: false,
      };
    }
    if (apptStatus === 'CONFIRMED') {
      const scheduledOn = new Date(appointment.startDateTime || '');
      const scheduledText = format(scheduledOn, 'MMM d, yyyy h:mma');
      return {
        status: `Confirmed for ${scheduledText}`,
        isRequest: false,
      };
    }
    if (apptStatus === 'SCHEDULED') {
      const scheduledOn = new Date(appointment.startDateTime || '');
      const scheduledText = format(scheduledOn, 'MMM d, yyyy h:mma');
      return {
        status: `Scheduled for ${scheduledText}`,
        isRequest: false,
      };
    }
    if (apptStatus === 'CANCELED') {
      const acceptedOn = new Date(appointment.updatedAt || '');
      const acceptedText = format(acceptedOn, 'MMM d, yyyy');
      return {
        status: `Canceled on ${acceptedText}`,
        isRequest: false,
      };
    }
    if (apptStatus === 'ARCHIVED') {
      const archivedOn = new Date(
        appointment.archivedAt || appointment.updatedAt || '',
      );
      const archivedText = format(archivedOn, 'MMM d, yyyy');
      return {
        status: `Archived on ${archivedText}`,
        isRequest: false,
      };
    }
    if ((appointment as unknown as GeneralRequests).requestId) {
      const requestedOn = new Date(appointment.createdAt || '');
      const requestedText = format(requestedOn, 'MMM d, yyyy');
      return {
        status: `Requested on ${requestedText}`,
        isRequest: true,
      };
    }
    return DEFAULT_HEADER;
  }, [appointment]);

  const { header, subHeader } = React.useMemo(
    () => ({
      header: getParticipantDisplayName(customerParticipant, true),
      subHeader: `Chat with: ${participantsString}`,
    }),
    [customerParticipant, participantsString],
  );

  const url = appointment
    ? `${ROUTES[isRequest ? 'requests' : 'calendar']}/${
        'requestId' in appointment && appointment.requestId
          ? appointment.requestId
          : appointment?.appointmentId
      }`
    : '';

  const actions = React.useMemo<FooterAction[]>(() => {
    if (!appointment && appointmentId) return [];
    const customerId =
      appointment?.customerId || getParticipantId(customerParticipant?.userId);
    const tempActions = [
      {
        label: 'View Customer',
        action: () => navigate(`${ROUTES.customers}/${customerId}`),
      },
      {
        label: participation?.notify ? 'Mute Chat' : 'Unmute Chat',
        action: () =>
          muteChat({
            conversationId: conversation.id,
            muted: !!participation?.notify,
          }),
      },
      {
        label: isArchived ? 'Unarchive Chat' : 'Archive Chat',
        action: () =>
          archiveChat({
            conversationId: conversation.id,
            archived: !isArchived,
          }),
      },
    ].filter(Boolean) as FooterAction[];

    if (appointment) {
      tempActions.unshift({
        label: 'View Request',
        action: () => navigate(url),
      });
    }

    return tempActions;
  }, [
    appointment,
    appointmentId,
    archiveChat,
    conversation.id,
    customerParticipant?.userId,
    isArchived,
    muteChat,
    navigate,
    participation?.notify,
    url,
  ]);

  return (
    <Box
      p={3}
      zIndex={1}
      boxShadow="0px 4px 13px 0px #0000001A;"
      display="flex"
      alignItems="center"
      gap={1}>
      <Button
        onClick={onBack}
        sx={{
          minWidth: 0,
          height: '100%',
          p: 0,
          px: 1,
          display: {
            xs: 'block',
            md: 'none',
          },
        }}>
        <ArrowBackIosRounded
          htmlColor={theme.palette.darkGrey.main}
          width={20}
        />
      </Button>

      {!isLoading ? (
        <Avatar
          src={appointmentImage || undefined}
          alt={customerInitials}
          sx={{
            bgcolor: theme.palette.darkerLightGrey.main,
            width: 40,
            height: 40,
          }}>
          {!appointmentImage && (
            <Typography
              color={theme.palette.medGrey.main}
              variant="body2"
              fontWeight="bold">
              {customerInitials}
            </Typography>
          )}
        </Avatar>
      ) : (
        <Skeleton variant="circular" width={40} height={40} />
      )}

      <Stack gap={0.5} flex={1}>
        {!!appointment && !isLoading && (
          <>
            <Typography
              variant="h5"
              component="p"
              color={theme.palette.darkGrey.main}>
              {isRequest ? 'Request' : 'Appointment'} with {customerName}
            </Typography>
            <Typography variant="body2" color={theme.palette.darkGrey.main}>
              {status}
            </Typography>
            <Typography variant="body2" color={theme.palette.darkGrey.main}>
              {subHeader}
              {isArchived && (
                <Typography
                  component="span"
                  variant="inherit"
                  color={theme.palette.medGrey.main}>
                  {' '}
                  (Archived)
                </Typography>
              )}
            </Typography>
          </>
        )}
        {isLoading && (
          <>
            <Skeleton variant="text" width={120} height={17.5} />
            <Skeleton variant="text" width={100} height={17} />
            <Skeleton variant="text" width={100} height={17} />
          </>
        )}
        {!appointment && !isLoading && (
          <>
            <Typography
              variant="h5"
              component="p"
              color={theme.palette.darkGrey.main}>
              {header}
            </Typography>
            <Typography variant="body2" color={theme.palette.darkGrey.main}>
              {subHeader}
            </Typography>
          </>
        )}
      </Stack>

      {!!actions?.length && !isLoading && (
        <DropdownMenu
          isLoading={isPending}
          options={actions}
          button={{
            horizontal: true,
            disableRipple: true,
            sx: {
              p: important(theme.spacing(1)),
              flexShrink: 0,
              ':hover': {
                bgcolor: 'transparent',
              },
            },
          }}
        />
      )}
    </Box>
  );
}

export default ChatBoxHeader;
